import AppContent from '@src/components/common/container/AppContent';
import withLocalize, { IWithLocalizeOwnProps } from '@src/components/common/localize/withLocalize';
import EmailTemplateListView from '@src/components/emailtemplate/view/EmailTemplateListView';
import { IEmailTemplate } from '@src/model/emailtemplate/EmailTemplate';
import { ICollectionData, ICollectionFetchPayload, IUserFeedbackMessagePayload, UserFeedbackMessageSeverity, UserFeedbackMessageType } from '@src/service/business/common/types';
import UserFeedbackBusinessStore from '@src/service/business/common/userFeedbackBusinessProvider';
import { EmailTemplateBusinessStore, IEmailTemplateListFilter } from '@src/service/business/emailtemplates/EmailTemplateBusinessStore';
import AppConfigService from '@src/service/common/AppConfigService';
import { createTrackableAction, ITrackableAction } from '@src/service/util/action/trackAction';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter, WithRouterProps } from 'react-router';

const DEFAULT_SORT_VALUE = 'title,asc';

// -- Prop types
// ----------
export interface IAdminEmailTemplateListContainerOwnProps {

}

export interface IAdminEmailTemplateListContainerStateProps {
  emailTemplateListFilter: IEmailTemplateListFilter;
  emailTemplateList: ICollectionData<IEmailTemplate>;
}

export interface IAdminEmailTemplateListContainerDispatchProps {
  fetchEmailTemplateList: (filter: ICollectionFetchPayload<IEmailTemplateListFilter>) => void;
  deleteEmailTemplate: (emailTemplateId: string) => ITrackableAction;
  storeEmailTemplateListFIlter: (filter: IEmailTemplateListFilter) => void;
  clearEmailTemplateListFilter: () => void;
  reportMessage: (data: IUserFeedbackMessagePayload) => void;
}

type IAdminEmailTemplateListContainerProps = IAdminEmailTemplateListContainerOwnProps & IAdminEmailTemplateListContainerStateProps & IAdminEmailTemplateListContainerDispatchProps & IWithLocalizeOwnProps & WithRouterProps;

// --
// ----- State types
interface IAdminEmailTemplateListContainerState {
  page: number;
  size: number;
  sort: string;
}

// -- Component
// ----------
class AdminEmailTemplateListContainer extends React.Component<IAdminEmailTemplateListContainerProps, IAdminEmailTemplateListContainerState> {
  state: IAdminEmailTemplateListContainerState = {
    page: 0,
    size: AppConfigService.getValue('api.paging.defaultPageSize'),
    sort: DEFAULT_SORT_VALUE,
  };

  componentDidMount() {
    this.fetchEmailTemplateList();
  }

  componentDidUpdate = (prevProps: IAdminEmailTemplateListContainerProps, prevState: IAdminEmailTemplateListContainerState) => {
    if (this.state.page !== prevState.page || this.state.size !== prevState.size || this.state.sort !== prevState.sort || this.props.emailTemplateListFilter !== prevProps.emailTemplateListFilter) {
      this.fetchEmailTemplateList();
    }
  };

  componentWillUnmount() {
    this.props.clearEmailTemplateListFilter();
  }

  render() {
    return (
      <AppContent>
        <EmailTemplateListView
          emailTemplateList={this.props.emailTemplateList}
          showControls={true}
          onDelete={this.handleDelete}
          onFilterChange={this.handleFilterChange}
          emailTemplateListViewFilter={this.props.emailTemplateListFilter}
          handlePageChange={this.handlePageChange}
          onChange={this.fetchEmailTemplateList}
        />
      </AppContent>
    );
  }

  handlePageChange = (page: number, pageSize?: number) => {
    this.setState({ page: page - 1, size: pageSize ?? this.state.size });
  };

  handleFilterChange = (filter: IEmailTemplateListFilter) => {
    this.props.storeEmailTemplateListFIlter(filter);
    this.setState({ page: 0 });
  };

  handleDelete = (emailTemplate: IEmailTemplate) => {
    this.props.deleteEmailTemplate(emailTemplate.id).track().subscribe(
      // success
      () => {
        this.fetchEmailTemplateList();
        this.props.reportMessage({ message: this.props.translate('EMAIL_TEMPLATE.TEMPLATE_DELETED_MESSAGE'), type: UserFeedbackMessageType.NOTIFICATION, severity: UserFeedbackMessageSeverity.INFO });
      }
    );
  };

  fetchEmailTemplateList = () => {
    this.props.fetchEmailTemplateList(
      {
        page: this.state.page,
        sort: [this.state.sort],
        size: this.state.size,
        filter: this.props.emailTemplateListFilter,
      }
    );
  };

}

// -- HOCs and exports
// ----------

// `state` parameter needs a type annotation to type-check the correct shape of a state object but also it'll be used by "type inference" to infer the type of returned props
const mapStateToProps = (state: any, ownProps: IAdminEmailTemplateListContainerOwnProps): IAdminEmailTemplateListContainerStateProps => ({
  emailTemplateList: EmailTemplateBusinessStore.selectors.getEmailTemplateList(state),
  emailTemplateListFilter: EmailTemplateBusinessStore.selectors.getEmailTemplateListFilter(state),
});

// `dispatch` parameter needs a type annotation to type-check the correct shape of an action object when using dispatch function
const mapDispatchToProps = (dispatch: any): IAdminEmailTemplateListContainerDispatchProps => ({
  fetchEmailTemplateList: (filter: ICollectionFetchPayload<IEmailTemplateListFilter>) => dispatch(EmailTemplateBusinessStore.actions.fetchEmailTemplateList(filter)),
  deleteEmailTemplate: (emailTemplateId: string) => createTrackableAction(dispatch(EmailTemplateBusinessStore.actions.deleteEmailTemplate(emailTemplateId))),
  storeEmailTemplateListFIlter: (filter: IEmailTemplateListFilter) => dispatch(EmailTemplateBusinessStore.actions.storeEmailTemplateListFilter(filter)),
  clearEmailTemplateListFilter: () => dispatch(EmailTemplateBusinessStore.actions.clearEmailTemplateListFilter()),
  reportMessage: (data: IUserFeedbackMessagePayload) => dispatch(UserFeedbackBusinessStore.actions.reportMessage(data)),
});

export default connect<IAdminEmailTemplateListContainerStateProps, IAdminEmailTemplateListContainerDispatchProps, IAdminEmailTemplateListContainerOwnProps>(mapStateToProps, mapDispatchToProps)(withRouter(withLocalize(AdminEmailTemplateListContainer) as any));
